import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/PageHeader';
import { Kanban } from '@/components/Kanban';
import { EditDemandaModalV3 as EditDemandaModal } from '@/components/EditDemandaModalV3';
import { KanbanSkeleton } from '@/components/Skeleton';
import { api } from '@/lib/api';
import type { Area } from '@/lib/types';

// slug → nome no Airtable (regex que casa o nome real, com fallback)
const SLUG_TO_AREA: Record<string, { titulo: string; matcher: (a: Area) => boolean }> = {
  analise:   { titulo: 'Demandas · Análise',   matcher: (a) => /an[áa]lise/i.test(a.nome) },
  conteudo:  { titulo: 'Demandas · Conteúdo',  matcher: (a) => /conte[uú]do/i.test(a.nome) },
  branding:  { titulo: 'Demandas · Branding',  matcher: (a) => /branding|design/i.test(a.nome) },
  produtora: { titulo: 'Demandas · Produtora', matcher: (a) => /produtora/i.test(a.nome) },
  eventos:   { titulo: 'Demandas · Eventos',   matcher: (a) => /eventos?/i.test(a.nome) },
};

export function TimeArea() {
  const { area } = useParams<{ area: string }>();
  const slug = (area ?? '').toLowerCase();
  const def = SLUG_TO_AREA[slug];
  const [editId, setEditId] = useState<string | null>(null);

  const demandasQ = useQuery({ queryKey: ['demandas'],     queryFn: () => api.demandas.list().then((r) => r.data) });
  const solicitantesQ = useQuery({ queryKey: ['solicitantes'], queryFn: () => api.solicitantes.list().then((r) => r.data) });
  const pessoasQ = useQuery({ queryKey: ['pessoas'],       queryFn: () => api.pessoas.list().then((r) => r.data) });
  const areasQ = useQuery({ queryKey: ['areas'],           queryFn: () => api.areas.list().then((r) => r.data) });

  const demandas = demandasQ.data ?? [];
  const solicitantes = solicitantesQ.data ?? [];
  const pessoas = pessoasQ.data ?? [];
  const areas = areasQ.data ?? [];

  const areaAlvo = useMemo(() => {
    if (!def) return null;
    return areas.find(def.matcher) ?? null;
  }, [areas, def]);

  const filtradas = useMemo(() => {
    if (!areaAlvo) return [];
    return demandas.filter((d) => d.areasIds.includes(areaAlvo.id));
  }, [demandas, areaAlvo]);

  if (!def) {
    return (
      <div>
        <PageHeader title="Demandas" />
        <div className="px-6 py-8 text-sm text-muted">Área desconhecida: "{slug}".</div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title={def.titulo} />
      <div className="px-6 py-3">
        {!areaAlvo && !areasQ.isLoading && (
          <div className="card text-sm text-muted">
            Área "{def.titulo.replace('Demandas · ', '')}" não encontrada no Airtable.
          </div>
        )}
        {demandasQ.isLoading && <KanbanSkeleton />}
        {!demandasQ.isLoading && areaAlvo && (
          <>
            <div className="text-[11px] text-muted mb-2">
              {filtradas.length} demanda{filtradas.length === 1 ? '' : 's'} atribuída{filtradas.length === 1 ? '' : 's'} a esta área
            </div>
            <Kanban demandas={filtradas} solicitantes={solicitantes} areas={areas} onCardClick={setEditId} />
          </>
        )}
      </div>

      <EditDemandaModal
        demandaId={editId} demandas={demandas} solicitantes={solicitantes}
        pessoas={pessoas} areas={areas} onClose={() => setEditId(null)}
      />
    </div>
  );
}
